// Vendor libraries
import { EventEmitter, SimpleChanges } from '@angular/core';
// Interfaces / types
import { PaginationControls } from 'NewAngular/features/components/pagination/pagination.constants';
import * as i0 from "@angular/core";
import * as i1 from "../../../directives/disabled/disabled.directive";
/**
 * Pagination Controls Component
 *
 * This component provides generic functionality for pagination controls/buttons that are commonly used in components
 * that display large lists of items.
 *
 * Input Properties
 *  disableButtons: boolean used to control whether or not the pagination buttons should be clickable
 *  paginationInfo: PaginationInfo object used for computing the new page when a button is clicked
 *
 * Output Properties
 *  pageChangeHandler: EventEmitter that emits the new page number after the page has been changed
 *
 */
export class PaginationControlsComponent {
    constructor() {
        // If this input is true, then all buttons in this component will be disabled
        this.disableButtons = false;
        this.pageChangeHandler = new EventEmitter();
        this.PAGE_CONTROLS = PaginationControls;
        this.pageNum = 1;
        this.isInitialState = true;
    }
    ngOnInit() {
        if (this.paginationInfo) {
            this.pageNum = this.paginationInfo.current_page_num;
        }
    }
    ngOnChanges(changes) {
        if (changes.paginationInfo?.currentValue) {
            this.pageNum = changes.paginationInfo.currentValue.current_page_num;
            this.isInitialState = this.isInitialPaginationState(changes.paginationInfo.currentValue);
        }
    }
    isInitialPaginationState(paginationInfo) {
        // Check if all values are zeros in pagination info
        return Object.values(paginationInfo).every((value) => value === 0);
    }
    /**
     * Uses the current value of pageNum and the paginationEvent to determine the next page number.
     * @param paginationEvent the pagination button that was clicked
     */
    pageChanged(paginationEvent) {
        // skip `first` or `prev` if already on the first page.
        if ((paginationEvent === this.PAGE_CONTROLS.FIRST || paginationEvent === this.PAGE_CONTROLS.PREV) &&
            this.pageNum === 1)
            return;
        // skip `last` or `next` if already on the last page.
        if ((paginationEvent === this.PAGE_CONTROLS.LAST || paginationEvent === this.PAGE_CONTROLS.NEXT) &&
            this.pageNum >= this.paginationInfo.num_pages)
            return;
        let newPageNum;
        switch (paginationEvent) {
            case this.PAGE_CONTROLS.FIRST:
                newPageNum = 1;
                break;
            case this.PAGE_CONTROLS.PREV:
                newPageNum = Math.max(this.pageNum - 1, 1);
                break;
            case this.PAGE_CONTROLS.NEXT:
                newPageNum = Math.min(this.pageNum + 1, this.paginationInfo.num_pages);
                break;
            case this.PAGE_CONTROLS.LAST:
                newPageNum = this.paginationInfo.num_pages;
                break;
            default:
                newPageNum = 1;
        }
        this.pageNum = newPageNum;
        this.pageChangeHandler.emit(this.pageNum);
    }
}
PaginationControlsComponent.ɵfac = function PaginationControlsComponent_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || PaginationControlsComponent)(); };
PaginationControlsComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: PaginationControlsComponent, selectors: [["ycn-pagination-controls"]], inputs: { disableButtons: "disableButtons", paginationInfo: "paginationInfo" }, outputs: { pageChangeHandler: "pageChangeHandler" }, standalone: false, features: [i0.ɵɵNgOnChangesFeature], decls: 15, vars: 4, consts: [[1, "panel-action"], ["ycnDisabled", "", 1, "btn", "btn-secondary", 3, "click", "disabled"], [1, "divider-vertical"]], template: function PaginationControlsComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0)(1, "div", 1);
        i0.ɵɵlistener("click", function PaginationControlsComponent_Template_div_click_1_listener() { return ctx.pageChanged(ctx.PAGE_CONTROLS.FIRST); });
        i0.ɵɵtext(2, "First");
        i0.ɵɵelementEnd()();
        i0.ɵɵelement(3, "div", 2);
        i0.ɵɵelementStart(4, "div", 0)(5, "div", 1);
        i0.ɵɵlistener("click", function PaginationControlsComponent_Template_div_click_5_listener() { return ctx.pageChanged(ctx.PAGE_CONTROLS.PREV); });
        i0.ɵɵtext(6, "Prev");
        i0.ɵɵelementEnd()();
        i0.ɵɵelement(7, "div", 2);
        i0.ɵɵelementStart(8, "div", 0)(9, "div", 1);
        i0.ɵɵlistener("click", function PaginationControlsComponent_Template_div_click_9_listener() { return ctx.pageChanged(ctx.PAGE_CONTROLS.NEXT); });
        i0.ɵɵtext(10, "Next");
        i0.ɵɵelementEnd()();
        i0.ɵɵelement(11, "div", 2);
        i0.ɵɵelementStart(12, "div", 0)(13, "div", 1);
        i0.ɵɵlistener("click", function PaginationControlsComponent_Template_div_click_13_listener() { return ctx.pageChanged(ctx.PAGE_CONTROLS.LAST); });
        i0.ɵɵtext(14, "Last");
        i0.ɵɵelementEnd()();
    } if (rf & 2) {
        i0.ɵɵadvance();
        i0.ɵɵproperty("disabled", ctx.pageNum === 1 || ctx.disableButtons || ctx.isInitialState);
        i0.ɵɵadvance(4);
        i0.ɵɵproperty("disabled", ctx.pageNum === 1 || ctx.disableButtons || ctx.isInitialState);
        i0.ɵɵadvance(4);
        i0.ɵɵproperty("disabled", ctx.pageNum >= ctx.paginationInfo.num_pages || ctx.disableButtons || ctx.isInitialState);
        i0.ɵɵadvance(4);
        i0.ɵɵproperty("disabled", ctx.pageNum >= ctx.paginationInfo.num_pages || ctx.disableButtons || ctx.isInitialState);
    } }, dependencies: [i1.DisabledDirective], encapsulation: 2 });
